import haversine from "haversine-distance";
import {
  calculateTrackElevationStats,
  sanitizeElevationPoints,
} from "./elevationStats.js";
import { filterAndroidTrackPoints } from "./androidTrackFilter.js";

export function prepareRecordingMetricPoints(points, options = {}) {
  let list = Array.isArray(points) ? points.filter(Boolean) : [];
  if (options.android === true && list.length >= 2) {
    list = filterAndroidTrackPoints(list);
  }

  if (list.length >= 2) {
    const beforeDistanceFilter = list;
    const filtered = [list[0]];
    let last = list[0];
    for (let index = 1; index < list.length; index += 1) {
      const current = list[index];
      const stepMeters = haversine(
        { lat: Number(last?.lat), lon: Number(last?.lon ?? last?.lng) },
        { lat: Number(current?.lat), lon: Number(current?.lon ?? current?.lng) }
      );
      if (Number.isFinite(stepMeters) && stepMeters >= 2) {
        filtered.push(current);
        last = current;
      }
    }
    list = filtered.length >= 2 ? filtered : beforeDistanceFilter;
  }

  return sanitizeElevationPoints(list, {
    maxDelta: 30,
    maxVerticalSpeed: 0.7,
    minVerticalAllowance: 3,
  });
}

function pointTimestamp(point) {
  const value = Number(point?.ts ?? point?.time ?? 0);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return value < 1e12 ? value * 1000 : value;
}

export function calculateRecordingMetrics(points, options = {}) {
  const list = Array.isArray(points) ? points : [];
  let distanceMeters = 0;
  let movingSeconds = 0;

  for (let index = 1; index < list.length; index += 1) {
    const previous = list[index - 1];
    const current = list[index];
    const previousTs = pointTimestamp(previous);
    const currentTs = pointTimestamp(current);
    const elapsedSeconds =
      previousTs && currentTs ? (currentTs - previousTs) / 1000 : 0;
    const stepMeters = haversine(
      { lat: Number(previous?.lat), lon: Number(previous?.lon ?? previous?.lng) },
      { lat: Number(current?.lat), lon: Number(current?.lon ?? current?.lng) }
    );
    if (!Number.isFinite(stepMeters)) continue;
    if (elapsedSeconds > 300 && stepMeters > 2000) continue;

    distanceMeters += stepMeters;
    if (elapsedSeconds > 0 && elapsedSeconds <= 300) {
      const speedKmh = (stepMeters / elapsedSeconds) * 3.6;
      if (speedKmh > 1.2) movingSeconds += elapsedSeconds;
    }
  }

  const startedAt = Number(options?.startedAt || 0);
  const stoppedAt = Number(options?.stoppedAt || options?.now || Date.now());
  const pausedMilliseconds = Math.max(0, Number(options?.pausedMilliseconds || 0));
  const pointsStart = pointTimestamp(list[0]);
  const pointsEnd = pointTimestamp(list[list.length - 1]);
  const elapsedFromRecording =
    startedAt > 0 && stoppedAt >= startedAt
      ? Math.max(0, (stoppedAt - startedAt - pausedMilliseconds) / 1000)
      : 0;
  const elapsedFromPoints =
    pointsStart > 0 && pointsEnd >= pointsStart ? (pointsEnd - pointsStart) / 1000 : 0;
  const totalSeconds = Math.max(elapsedFromRecording, elapsedFromPoints, movingSeconds);
  const stoppedSeconds = Math.max(0, totalSeconds - movingSeconds);
  const distanceKm = distanceMeters / 1000;
  const averageMovingKmh =
    distanceKm > 0 && movingSeconds > 0
      ? distanceKm / (movingSeconds / 3600)
      : 0;

  return {
    distanceKm,
    totalSeconds,
    movingSeconds,
    stoppedSeconds,
    averageMovingKmh,
    elevationGain: calculateTrackElevationStats(list).gain,
  };
}

export function hasCanonicalRecordingMetrics(entry, version = 3) {
  const stats = entry?.stats;
  if (!stats || Number(stats.calculationVersion || 0) < version) return false;

  return [
    stats.distKm,
    stats.dPlus,
    stats.totalMin,
    stats.movingMin,
    stats.stoppedMin,
    stats.speed,
  ].every((value) => Number.isFinite(Number(value)) && Number(value) >= 0);
}
